import type { Messages } from './messages';

/**
 * Copy for `src/app/audit/`. Owned by that page's author, and by nobody else.
 *
 * Every key starts with `audit.` — the compiler refuses anything else, so
 * this file cannot collide with another page's. Shared words (save, cancel,
 * retry, statuses, role names) are already in `common.ts` and
 * `vocabulary.ts`; use those rather than restating them here.
 *
 * `audit.trail.*`, `audit.col.*`, `audit.detail.*` and `audit.integrity.*`
 * are the shared trail component's (`src/app/audit/trail.tsx`), which the
 * المنشأة's audit tab also renders. What differs between the two trails is
 * passed in by each page from its own catalogue.
 */
export const audit = {
  'audit.title': 'سجل التدقيق',
  'audit.subtitle': 'ما فعله مشغّلو المنصّة، الأحدث أولًا. ما فعله أعضاء منشأة بعينها تجده في تبويب «التدقيق» داخلها.',
  'audit.caption': 'سجل أفعال المشغّلين',
  'audit.actorHeader': 'المشغّل',
  'audit.actorSearch': 'بريد المشغّل',
  'audit.noActor': 'بلا حساب — محاولة دخول فاشلة',
  'audit.emptyBody': 'لم يُسجَّل أي فعل لمشغّل بعد.',

  /* The filter bar. */
  'audit.trail.action': 'الإجراء',
  'audit.trail.actionAll': 'كل الإجراءات',
  'audit.trail.actionsLoading': 'جارٍ تحميل الإجراءات…',
  'audit.trail.actionsFailed': 'تعذّر تحميل قائمة الإجراءات: {message}',
  'audit.trail.actorHint': 'يكفي جزء من البريد.',
  'audit.trail.from': 'من يوم',
  'audit.trail.to': 'إلى يوم',
  'audit.trail.rangeHint': 'بتوقيت الرياض، ويشمل اليومين.',
  'audit.trail.toBeforeFrom': 'يوم النهاية قبل يوم البداية.',
  'audit.trail.onlyActor': 'حساب واحد',
  'audit.trail.onlyActorClear': 'إلغاء حصر السجل في هذا الحساب',
  'audit.trail.tenantPick': 'تصفية حسب منشأة',
  'audit.trail.tenantPickTitle': 'حصر السجل في منشأة',
  'audit.trail.tenantPickBody': 'يُعرض ما فعله مشغّلو المنصّة بهذه المنشأة فقط. منشأة حُذفت تبقى أفعالها في السجل، فتُختار من صف فيه.',
  'audit.trail.onlyTenant': 'منشأة واحدة:',
  'audit.trail.onlyTenantClear': 'إلغاء حصر السجل في هذه المنشأة',
  'audit.trail.export': 'تصدير المعروض (CSV)',
  'audit.trail.exportHint': 'يصدّر السجلات المحمّلة حتى الآن بعد التصفية، لا السجل كله. حمّل المزيد أولًا إن احتجت.',
  'audit.trail.exported': 'نُزّل الملف: {count} سجل.',

  /* The table. */
  'audit.col.when': 'الوقت',
  'audit.col.action': 'الإجراء',
  'audit.col.target': 'الهدف',
  'audit.col.tenant': 'المنشأة',
  'audit.col.ip': 'عنوان IP',
  'audit.trail.openDetails': 'عرض التفاصيل',
  'audit.trail.emptyTitle': 'لا سجلات تطابق',
  'audit.trail.emptyNone': 'لا سجلات بعد',
  'audit.trail.emptyFiltered': 'لا شيء في هذا السجل يطابق التصفية الحالية. وسّع نطاق الأيام أو امسح التصفية.',
  'audit.trail.invalidTitle': 'نطاق الأيام غير صالح',
  'audit.trail.invalidBody': 'اجعل يوم البداية قبل يوم النهاية أو مساويًا له، ثم يُعرض السجل.',
  'audit.trail.moreFailed': 'تعذّر تحميل المزيد',

  /* The detail drawer. */
  'audit.detail.description': 'سجل رقم {id}',
  'audit.detail.when': 'الوقت (الرياض)',
  'audit.detail.iso': 'الوقت الدقيق (UTC)',
  'audit.detail.actor': 'المنفّذ',
  'audit.detail.action': 'الإجراء',
  'audit.detail.code': 'رمز الإجراء',
  'audit.detail.resourceType': 'نوع الهدف',
  'audit.detail.resourceId': 'معرّف الهدف',
  'audit.detail.tenant': 'المنشأة',
  'audit.detail.ip': 'عنوان IP',
  'audit.detail.id': 'رقم السجل',
  'audit.detail.identifiers': 'المعرّفات',
  'audit.detail.metadata': 'البيانات المرفقة',
  'audit.detail.metadataEmpty': 'لا بيانات مرفقة بهذا السجل.',
  'audit.detail.value': 'القيمة',
  'audit.detail.secret': 'محجوب — يشبه سرًّا',
  'audit.detail.content': 'محجوب — محتوى المنشأة',
  'audit.detail.withheldSecret':
    'حُجبت قيم تشبه الأسرار (كلمة مرور أو رمز أو مفتاح) — عددها {count}. الحجب احتياط من اللوحة؛ الخادم لا يُفترض أن يرسلها أصلًا، فأبلغ عن هذا السجل.',
  'audit.detail.withheldContent':
    'حُجبت قيم تبدو من محتوى المنشأة (عنوان أو اسم عميل أو نص بحث أو نص مكتوب) — عددها {count}. محتوى المنشأة غير مقروء من لوحة المنصّة.',
  'audit.detail.onlyActor': 'أفعال هذا الحساب فقط',
  'audit.detail.onlyTenant': 'ما يخص هذه المنشأة فقط',
  'audit.detail.traceId': 'معرّف الطلب (traceId)',
  'audit.detail.traceIdHint': 'المعرّف نفسه في ردّ ذلك الطلب وفي سطور السجلات: انسخه للبحث في السجلات.',
  'audit.detail.traceIdNone': 'غير مسجَّل — صف أقدم من هذا الحقل',
  'audit.detail.openTenant': 'فتح المنشأة',

  /* Chain integrity. */
  'audit.integrity.title': 'روابط السلسلة',
  'audit.integrity.description':
    'كل سجل يحمل بصمة السجل الذي قبله. الفحص يتحقق من هذه الروابط وحدها — أن كل سجل يشير إلى الذي قبله — لا من مضمون كل سجل. يقرأ السجل كله، لذلك يجري حين تطلبه فقط.',
  'audit.integrity.run': 'التحقق من الروابط',
  'audit.integrity.rerun': 'إعادة التحقق',
  'audit.integrity.notRun': 'لم يُتحقق في هذه الجلسة',
  'audit.integrity.linksVerified': 'تم التحقق من الروابط',
  'audit.integrity.linksVerifiedBody':
    'قُرئ السجل كله، وكل سجل فيه يشير إلى السجل الذي قبله. هذا تحقق من الروابط فقط، لا شهادة بأن مضمون السجلات لم يتغيّر.',
  'audit.integrity.unchecked': 'لم يكتمل التحقق',
  'audit.integrity.uncheckedBody':
    'توقّف الفحص عند حدّه الأقصى من السجلات قبل أن يبلغ آخر السجل، فلم يُتحقق من الروابط كلها. هذه ليست «تم التحقق» ولا إنذارًا بعبث: لم يُعثر على رابط مقطوع فيما قُرئ، والباقي لم يُفحص. صعّد الأمر إلى فريق المنصّة إن احتجت فحصًا كاملًا.',
  'audit.integrity.broken': 'رابط مقطوع عند السجل {id}',
  'audit.integrity.brokenUnknown': 'رابط مقطوع في السلسلة',
  'audit.integrity.brokenBody':
    'السجل رقم {id} لا يشير إلى السجل الذي قبله، أي أن سجلًا حُذف أو أُدرج أو عُدّلت بصمته من خارج التطبيق. لا تعدّل شيئًا، وصعّد الأمر إلى فريق الأمن مع رقم السجل والتفصيل التقني أدناه.',
  'audit.integrity.reason': 'التفصيل التقني',
  'audit.integrity.unchained': 'غير مسلسل',
  'audit.integrity.unchainedBody':
    'هذا السجل غير مربوط بسلسلة بصمات، فلا روابط يُتحقق منها. النتيجة ليست «تم التحقق» ولا «مقطوعة».',
  'audit.integrity.checked': 'السجلات المفحوصة: {count}',
  'audit.integrity.checkedAt': 'آخر فحص: {relative}',
  'audit.integrity.failed': 'تعذّر الفحص',
  'audit.integrity.throttledTitle': 'طلبات فحص كثيرة',
  'audit.integrity.throttled': 'يُسمح بستة فحوص في الدقيقة. أعد المحاولة بعد {seconds} ثانية.',
  'audit.integrity.throttledButton': 'انتظر {seconds} ث',
} as const satisfies Messages<'audit'>;
